<template>
  <div v-if="visibleSchema.length" class="zone-coach-context">
    <div class="zcc-head">
      <span class="zcc-title"><i class="bi bi-robot"></i> {{ $t('zones.coachContext.title') }}</span>
      <i18n-t keypath="zones.coachContext.intro" tag="small" scope="global">
        <template #notSet><b>{{ $t('zones.coachContext.notSet') }}</b></template>
      </i18n-t>
    </div>

    <div v-for="attr in visibleSchema" :key="attr.key" class="zcc-attr">
      <div class="zcc-label">
        <span>{{ attr.label }}</span>
        <span v-if="isConfirmed(attr.key)" class="zcc-badge ok" :title="$t('zones.coachContext.confirmedTip')"><i class="bi bi-check-lg"></i> {{ $t('zones.coachContext.confirmed') }}</span>
        <!-- Three states, not two. An attribute with NO value is not a guess: the
             deriver declines rather than fall back to the first option, because a
             wrong fixture is worse than none — it is what had a clothing department
             being advised to move its shelf. Calling that "guessed" would put the
             lie back in one layer up. -->
        <span v-else-if="hasValue(attr.key)" class="zcc-badge guess" :title="$t('zones.coachContext.guessedTip')">{{ $t('zones.coachContext.guessed') }}</span>
        <span v-else class="zcc-badge none" :title="$t('zones.coachContext.notSetTip')">{{ $t('zones.coachContext.notSet') }}</span>
      </div>
      <p v-if="attr.help" class="zcc-help">{{ attr.help }}</p>
      <p v-if="!hasValue(attr.key)" class="zcc-help zcc-unset">
        {{ $t('zones.coachContext.unset') }}
      </p>
      <div class="zcc-chips">
        <button
          v-for="opt in attr.options"
          :key="opt.value"
          type="button"
          :class="['zcc-chip', {
            active: valueOf(attr.key) === opt.value,
            guessed: valueOf(attr.key) === opt.value && !isConfirmed(attr.key),
          }]"
          @click="confirm(attr.key, opt.value)"
        >{{ opt.label }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useCompanyStore } from '@/stores/CompanyStore'

// modelValue is the zone's coachContext object ({key: {value, source}}), owned by
// the parent settings modal — edits here ride along on its Save (no separate write).
const props = defineProps({
  // An existing zone's id. Absent while a zone is being drawn — the schema and
  // guesses are then asked for by type + name instead, so the panel is there at the
  // moment the person can actually answer it.
  zoneId: { type: [Number, String], default: null },
  zoneTypeId: { type: Number, default: null },
  zoneName: { type: String, default: '' },
  geometry: { type: String, default: null },     // 'polygon' | 'tripwire'
  modelValue: { type: Object, default: () => ({}) },
})
const emit = defineEmits(['update:modelValue'])
const companyStore = useCompanyStore()

const schema = ref([])
const guesses = ref({})   // {key: derived value} — display fallback for unconfirmed attrs

const isConfirmed = (key) => !!(props.modelValue && props.modelValue[key])
const valueOf = (key) => (props.modelValue?.[key]?.value) ?? guesses.value[key]
/** Is anything known at all — confirmed or derived? Attributes the deriver declines
 *  to guess at come back with no value, and must not be dressed up as a guess. */
const hasValue = (key) => valueOf(key) !== undefined && valueOf(key) !== null
const attrVisible = (attr) =>
  !attr.when || Object.entries(attr.when).every(([k, v]) => valueOf(k) === v)
const visibleSchema = computed(() => schema.value.filter(attrVisible))

function confirm(key, value) {
  emit('update:modelValue', { ...(props.modelValue || {}), [key]: { value, source: 'confirmed' } })
}

onMounted(async () => {
  try {
    const data = props.zoneId
      ? await companyStore.fetchZoneContext(props.zoneId)            // saved zone
      : await companyStore.proposeZoneContext({                      // still being drawn
        type: props.zoneTypeId, name: props.zoneName, geometry: props.geometry,
      })
    schema.value = data.schema || []
    guesses.value = Object.fromEntries(
      Object.entries(data.context || {}).map(([k, v]) => [k, v && v.value]))
  } catch (_) {
    schema.value = []
  }
})
</script>

<style scoped>
.zone-coach-context {
  border: 1px solid #e6edf3; border-radius: 10px; background: #f7fafc;
  padding: 14px 16px; margin: 4px 0 16px;
}
.zcc-head { display: flex; flex-direction: column; gap: 2px; margin-bottom: 10px; }
.zcc-title { font-weight: 700; color: #4F7EB3; font-size: 0.95rem; }
.zcc-head small { color: #7a8a99; font-size: 0.78rem; }
.zcc-attr { padding: 8px 0; border-top: 1px solid #eef2f6; }
.zcc-attr:first-of-type { border-top: 0; }
.zcc-label { display: flex; align-items: center; gap: 8px; font-weight: 600; font-size: 0.86rem; color: #344054; }
.zcc-help { font-size: 0.76rem; color: #8899a6; margin: 2px 0 6px; }
.zcc-badge { font-size: 0.66rem; font-weight: 700; border-radius: 999px; padding: 1px 7px; text-transform: uppercase; letter-spacing: .03em; }
.zcc-badge.guess { color: #9a6a1f; background: #fbf1df; }
.zcc-badge.none { color: #6b7a87; background: #eef2f6; }
.zcc-unset { color: #6b7a87; font-style: italic; }
.zcc-badge.ok { color: #2C6F58; background: #e7f4ee; }
.zcc-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.zcc-chip {
  border: 1px solid #d6e0ea; background: #fff; color: #425461;
  border-radius: 999px; padding: 4px 12px; font-size: 0.8rem; cursor: pointer; transition: all .15s ease;
}
.zcc-chip:hover { border-color: #4F7EB3; }
.zcc-chip.active { background: #4F7EB3; border-color: #4F7EB3; color: #fff; }
.zcc-chip.active.guessed { background: #fff; color: #9a6a1f; border-color: #e0b874; border-style: dashed; }
</style>
